import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Table, Badge, Collapse } from "reactstrap";
import "./ProjectsListTable.css";

const ProjectsListTable = ({ data }) => {
  const [expandedStreams, setExpandedStreams] = useState({});

  if (!data || data.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        <p className="mb-0">📊 No hay proyectos en el rango de fechas seleccionado</p>
        <small>Intenta ajustar el rango de fechas o los filtros</small>
      </div>
    );
  }

  const toggleStream = (valueStream) => {
    setExpandedStreams((prev) => ({
      ...prev,
      [valueStream]: !prev[valueStream],
    }));
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) return "$0.00";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const normalizeStatus = (status) => {
    const statusMap = {
      'Activo': 'Active',
      'Active': 'Active',
      'Terminado': 'Completed',
      'Completed': 'Completed',
      'Finalizado': 'Completed',
      'Pendiente': 'Pending',
      'Pending': 'Pending',
      'Cancelado': 'Cancelled',
      'Cancelled': 'Cancelled',
      'En Progreso': 'Active',
      'In Progress': 'Active',
    };
    return statusMap[status] || status;
  };

  const getStatusBadge = (status) => {
    const normalizedStatus = normalizeStatus(status);
    const statusColors = {
      'Active': 'success',
      'Completed': 'primary',
      'Pending': 'warning',
      'Cancelled': 'danger',
    };
    
    const statusLabels = {
      'Active': 'Activo',
      'Completed': 'Terminado',
      'Pending': 'Pendiente',
      'Cancelled': 'Cancelado',
    };

    return (
      <Badge color={statusColors[normalizedStatus] || "secondary"} pill>
        {statusLabels[normalizedStatus] || status}
      </Badge>
    );
  };

  const getMarginColor = (margin) => {
    const numMargin = parseFloat(margin);
    if (isNaN(numMargin)) return "text-muted";
    if (numMargin >= 30) return "text-success";
    if (numMargin >= 15) return "text-warning";
    return "text-danger";
  };

  const formatMargin = (margin) => {
    if (margin === null || margin === undefined) return "0.00";
    const num = parseFloat(margin);
    return isNaN(num) ? "0.00" : num.toFixed(2);
  };

  return (
    <div className="projects-list-table">
      {data.map((streamData) => {
        const isExpanded = expandedStreams[streamData.value_stream];
        const totals = streamData.totals || {
          count: streamData.projects?.length || 0,
          revenue: 0,
          cost: 0,
          profit: 0,
          margin: 0
        };

        return (
          <div key={streamData.value_stream} className="stream-section mb-3">
            {/* Value Stream Header */}
            <div
              className="stream-header"
              onClick={() => toggleStream(streamData.value_stream)}
            >
              <div className="stream-info">
                <span className={`chevron-icon ${isExpanded ? 'expanded' : ''}`}>
                  ▶
                </span>
                <h6 className="stream-title mb-0">
                  {streamData.value_stream}
                  <span className="text-muted ms-2">
                    ({totals.count} {totals.count === 1 ? "proyecto" : "proyectos"})
                  </span>
                </h6>
              </div>
              <div className="stream-metrics">
                <div className="metric">
                  <small className="metric-label">Revenue</small>
                  <strong className="metric-value">{formatCurrency(totals.revenue)}</strong>
                </div>
                <div className="metric">
                  <small className="metric-label">Cost</small>
                  <strong className="metric-value">{formatCurrency(totals.cost)}</strong>
                </div>
                <div className="metric">
                  <small className="metric-label">Profit</small>
                  <strong className={`metric-value ${totals.profit >= 0 ? "text-success" : "text-danger"}`}>
                    {formatCurrency(totals.profit)}
                  </strong>
                </div>
                <div className="metric">
                  <small className="metric-label">Margin</small>
                  <strong className={`metric-value ${getMarginColor(totals.margin)}`}>
                    {formatMargin(totals.margin)}%
                  </strong>
                </div>
              </div>
            </div>

            {/* Projects Table */}
            <Collapse isOpen={isExpanded}>
              <div className="projects-table-wrapper">
                <Table hover responsive size="sm" className="projects-table mb-0">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Proyecto</th>
                      <th>Fecha Registro</th>
                      <th>Status</th>
                      <th className="text-end">Revenue</th>
                      <th className="text-end">Cost</th>
                      <th className="text-end">Profit</th>
                      <th className="text-end">Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(streamData.projects || []).map((project) => (
                      <tr key={project.proyecto_code}>
                        <td>
                          <Link
                            to={`/detalle-proyectos/${project.proyecto_code}`}
                            className="project-code-link"
                          >
                            <code>{project.proyecto_code}</code>
                          </Link>
                        </td>
                        <td className="project-name-cell">
                          <Link
                            to={`/detalle-proyectos/${project.proyecto_code}`}
                            className="project-name-link"
                          >
                            {project.proyecto_name || project.name || 'Sin nombre'}
                          </Link>
                        </td>
                        <td className="date-cell">
                          <span className="date-badge">
                            📅 {formatDate(project.registration_date)}
                          </span>
                        </td>
                        <td>{getStatusBadge(project.status)}</td>
                        <td className="text-end">{formatCurrency(project.revenue)}</td>
                        <td className="text-end">{formatCurrency(project.cost)}</td>
                        <td className={`text-end fw-bold ${project.profit >= 0 ? "text-success" : "text-danger"}`}>
                          {formatCurrency(project.profit)}
                        </td>
                        <td className={`text-end fw-bold ${getMarginColor(project.margin)}`}>
                          {formatMargin(project.margin)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Collapse>
          </div>
        );
      })}

      {/* Overall Summary */}
      <div className="overall-summary">
        <div className="summary-content">
          <h6 className="summary-title mb-0">Total General</h6>
          <div className="summary-metrics">
            <div className="summary-metric">
              <small>Revenue</small>
              <strong>
                {formatCurrency(
                  data.reduce((sum, stream) => sum + (stream.totals?.revenue || 0), 0)
                )}
              </strong>
            </div>
            <div className="summary-metric">
              <small>Cost</small>
              <strong>
                {formatCurrency(
                  data.reduce((sum, stream) => sum + (stream.totals?.cost || 0), 0)
                )}
              </strong>
            </div>
            <div className="summary-metric">
              <small>Profit</small>
              <strong>
                {formatCurrency(
                  data.reduce((sum, stream) => sum + (stream.totals?.profit || 0), 0)
                )}
              </strong>
            </div>
            <div className="summary-metric">
              <small>Projects</small>
              <strong>
                {data.reduce((sum, stream) => sum + (stream.totals?.count || 0), 0)}
              </strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectsListTable;